import { ConfigurationItemEditor } from '@client/components/_core/configuration/ConfigurationItemEditor';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Space } from '@client/components/_ui/layout/Space';
import { useIsLiteOrg } from '@client/hooks/business/org/useIsLiteOrg';
import { useOrg } from '@client/hooks/business/org/useOrg';
import type { TimelineItem } from '@client/types/common';
import { COMPLAINT_MILESTONES } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { ExternalLink, Settings2 } from 'lucide-react';
import { ComplaintAcknowledgmentToggle } from './ComplaintAcknowledgmentToggle';

export const Route = createFileRoute('/console/$orgId/configuration/compliance/complaints/general')({
    staticData: {
        metadata: {
            title: 'General',
        },
        ui: {
            icon: Settings2,
            title: 'Complaint settings',
            subtitle: 'Lodgement link, acknowledgements and timeline',
            menuDescription: 'Lodgement link, acknowledgements and timeline',
        },
    },
    component: Complaints,
});

function Complaints() {
    const { id: orgId } = useOrg();
    // Curium Lite (CUR-379): acknowledgement email/SMS is removed on the Lite tier (not just
    // locked) — the server never sends them for Lite orgs, so the toggle is hidden entirely.
    const isLiteOrg = useIsLiteOrg();

    return (
        <PageController route={Route}>
            <Space orientation="vertical" size="middle" className="w-full">
                <Button.Link
                    to="/public/$orgId/complaint/new"
                    params={{ orgId }}
                    target="_blank"
                    variant="tertiary"
                    icon={<ExternalLink />}
                    iconPosition="end"
                >
                    Public complaint link
                </Button.Link>
                {!isLiteOrg && <ComplaintAcknowledgmentToggle />}
                <ConfigurationItemEditor
                    title="Complaint Timeline Items"
                    settingToEdit="COMPLAINT_TIMELINE_ITEMS"
                    description="Rename, add or remove a complaint timeline item."
                    editor={{ height: '300px' }}
                    onValidate={(value) => {
                        const items: TimelineItem[] = JSON.parse(value);
                        const existingInternalNames = new Set();

                        for (const item of items) {
                            if (existingInternalNames.has(item.internalName)) {
                                return new Error('Internal name must be unique. Duplicate found: ' + item.internalName);
                            }

                            const blackListedInternalNames = [
                                COMPLAINT_MILESTONES.registered.internalName,
                                COMPLAINT_MILESTONES.received.internalName,
                                COMPLAINT_MILESTONES.closed.internalName,
                            ];

                            if (blackListedInternalNames.includes(item.internalName)) {
                                return new Error('Internal name is reserved for system use: ' + item.internalName);
                            }

                            existingInternalNames.add(item.internalName);
                        }

                        return undefined;
                    }}
                />
            </Space>
        </PageController>
    );
}
